// src/hooks/useBoardPresets.ts
import { useState, useCallback } from "react";
import { Piece } from "../types/Board";
import { getSetupByName } from "../utils/boardSetup.ts";
import { analyzeAndSetPromotionPieces } from "../utils/gameHelpers.ts";
import { PIECE_DEFINITIONS } from "../data/pieceDefinitions.ts";

export interface GamePreset {
  id: string;
  name: string;
  rows: number;
  cols: number;
  setup: string;
}

interface CustomVariant {
  id: string;
  name: string;
  boardSize: number;
  whitePieces?: any[];
  blackPieces?: any[];
}

interface UseBoardPresetsProps {
  standardPresets: GamePreset[];
  customVariants: CustomVariant[];
  onBoardChange?: (
    pieces: Map<string, Piece>,
    rows: number,
    cols: number,
    promotionPieces: string[]
  ) => void;
}

export const useBoardPresets = ({
  standardPresets,
  customVariants,
  onBoardChange,
}: UseBoardPresetsProps) => {
  const [currentPresetId, setCurrentPresetId] = useState<string>("0808-001");

  /**
   * Get all available presets (standard + custom)
   */
  const getAllPresets = useCallback(() => {
    return [...standardPresets];
  }, [standardPresets]);

  /**
   * Get current preset details
   */
  const getCurrentPreset = useCallback((): GamePreset | CustomVariant | null => {
    // Check standard presets first
    const standardPreset = standardPresets.find((p) => p.id === currentPresetId);
    if (standardPreset) return standardPreset;

    // Check custom variants
    const customVariant = customVariants.find((v) => v.id === currentPresetId);
    if (customVariant) return customVariant;

    return null;
  }, [currentPresetId, standardPresets, customVariants]);

  /**
   * Load a standard preset
   */
  const loadStandardPreset = useCallback(
    (preset: GamePreset): Map<string, Piece> => {
      console.log(`📋 Loading standard preset: ${preset.name}`);

      const pieces = getSetupByName(preset.setup, preset.rows);
      const promotionPieces = analyzeAndSetPromotionPieces(pieces, preset.rows);

      if (onBoardChange) {
        onBoardChange(pieces, preset.rows, preset.cols, promotionPieces);
      }

      return pieces;
    },
    [onBoardChange]
  );

  /**
   * Load a custom variant
   */
  const loadCustomVariant = useCallback(
    (variant: CustomVariant): Map<string, Piece> => {
      console.log(`📋 Loading custom variant: ${variant.name}`);

      const customPieces = new Map<string, Piece>();

      // Load white pieces
      variant.whitePieces?.forEach((placedPiece: any) => {
        const definition = PIECE_DEFINITIONS[placedPiece.type.toLowerCase()];
        const piece: Piece = {
          type: placedPiece.type,
          color: "white",
          position: placedPiece.position,
          abbreviation: definition?.abbreviation || placedPiece.type.substring(0, 3).toUpperCase(),
          hasMoved: false,
          rules: definition?.rules || [],
        };
        customPieces.set(
          `${placedPiece.position.row}-${placedPiece.position.col}`,
          piece
        );
      });

      // Load black pieces
      variant.blackPieces?.forEach((placedPiece: any) => {
        const definition = PIECE_DEFINITIONS[placedPiece.type.toLowerCase()];
        const piece: Piece = {
          type: placedPiece.type,
          color: "black",
          position: placedPiece.position,
          abbreviation: definition?.abbreviation || placedPiece.type.substring(0, 3).toUpperCase(),
          hasMoved: false,
          rules: definition?.rules || [],
        };
        customPieces.set(
          `${placedPiece.position.row}-${placedPiece.position.col}`,
          piece
        );
      });

      const promotionPieces = analyzeAndSetPromotionPieces(
        customPieces,
        variant.boardSize
      );

      if (onBoardChange) {
        onBoardChange(
          customPieces,
          variant.boardSize,
          variant.boardSize,
          promotionPieces
        );
      }

      return customPieces;
    },
    [onBoardChange]
  );

  /**
   * Load a preset by ID (standard or custom)
   */
  const loadPreset = useCallback(
    (presetId: string): Map<string, Piece> | null => {
      setCurrentPresetId(presetId);

      // Try standard preset first
      const standardPreset = standardPresets.find((p) => p.id === presetId);
      if (standardPreset) {
        return loadStandardPreset(standardPreset);
      }

      // Try custom variant
      const customVariant = customVariants.find((v) => v.id === presetId);
      if (customVariant) {
        return loadCustomVariant(customVariant);
      }

      console.error(`❌ Preset not found: ${presetId}`);
      return null;
    },
    [standardPresets, customVariants, loadStandardPreset, loadCustomVariant]
  );

  /**
   * Get preset by ID without loading it
   */
  const getPresetById = useCallback(
    (presetId: string): GamePreset | CustomVariant | null => {
      const standardPreset = standardPresets.find((p) => p.id === presetId);
      if (standardPreset) return standardPreset;

      const customVariant = customVariants.find((v) => v.id === presetId);
      if (customVariant) return customVariant;

      return null;
    },
    [standardPresets, customVariants]
  );

  /**
   * Check if a preset ID is a standard preset
   */
  const isStandardPreset = useCallback(
    (presetId: string): boolean => {
      return standardPresets.some((p) => p.id === presetId);
    },
    [standardPresets]
  );

  /**
   * Check if a preset ID is a custom variant
   */
  const isCustomVariant = useCallback(
    (presetId: string): boolean => {
      return customVariants.some((v) => v.id === presetId);
    },
    [customVariants]
  );

  return {
    // State
    currentPresetId,
    currentPreset: getCurrentPreset(),

    // Getters
    getAllPresets,
    getPresetById,
    isStandardPreset,
    isCustomVariant,

    // Actions
    loadPreset,
    setCurrentPresetId,
  };
};
